import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useAuth } from "../../../Context/AuthContext";

interface Customer {
  id: string;
  mobile: number | string;
  is_active: number;
  amount: string | number;
  total_package_amount: string | number;
  currency: string;
  amount_spent: string | number;
  amount_without_currency?: string | number;
  total_package_amount_without_currency?: string | number;
  amount_spent_without_currency?: string | number;
}

interface CustomerWithFooter extends Customer {
  isFooter?: boolean;
}

const customStyles = {
  headCells: {
    style: {
      fontSize: "16px",
      fontWeight: "600",
      color: "#308e87",
      backgroundColor: "#f8f9fa",
      borderBottom: "2px solid #308e87",
    },
  },
  rows: {
    style: {
      borderBottom: "1px solid #dee2e6",
    },
  },
  table: {
    style: {
      border: "1px solid #dee2e6",
      borderRadius: "8px",
    },
  },
};

const conditionalRowStyles = [
  {
    when: (row: any) => row.isFooter === true,
    style: {
      fontWeight: "800",
      backgroundColor: "#f8f9fa",
      color: "#308e87",
      borderTop: "2px solid #dee2e6",
      fontSize: "16px",
    },
  },
];

export default function CustomerReport() {
  const token = localStorage.getItem("token");
  const { currency } = useCurrency();
  const { hasPermission } = useAuth();

  // State Management
  const [filterText, setFilterText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filteredItems, setFilteredItems] = useState<Customer[]>([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [grandTotals, setGrandTotals] = useState({
    totalPurchase: "0.00",
    totalSpent: "0.00",
    totalBalance: "0.00",
  });

  // Debounce search input changes (350ms delay) to prevent focus loss and excess API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filterText);
    }, 350);
    return () => clearTimeout(timer);
  }, [filterText]);

  // Main Data Fetcher — uses get_all_paginated for backend search + sorting support
  const fetchData = useCallback(
    async (page = currentPage, currentPerPage = perPage) => {
      setTableLoading(true);
      try {
        const response = await axios.get(
          `${baseUrl}customer_detail/get_all_paginated`,
          {
            params: {
              page,
              per_page: currentPerPage,
              search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
              sort_field: sortField,
              sort_direction: sortDir,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setFilteredItems(response.data.data || []);
        setTotalRows(response.data.total || 0);
        if (response.data.grand_totals) {
          const gt = response.data.grand_totals;
          setGrandTotals({
            totalPurchase: parseFloat(gt.total_package_amount || 0).toFixed(2),
            totalSpent: parseFloat(gt.amount_spent || 0).toFixed(2),
            totalBalance: parseFloat(gt.amount || 0).toFixed(2),
          });
        }
      } catch (error) {
        console.error("Error fetching customer report data:", error);
      } finally {
        setTableLoading(false);
      }
    },
    [currentPage, perPage, debouncedSearch, sortField, sortDir, token]
  );

  // Sort Handler
  const handleSort = useCallback(
    (column: any, direction: "asc" | "desc") => {
      setSortField(column.sortField || "created_at");
      setSortDir(direction);
      setCurrentPage(1);
    },
    []
  );

  // Auto-Fetch when pagination, debounced search, or sort changes
  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, debouncedSearch, sortField, sortDir]);

  // Pagination Handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
  };

  // Unpaginated Fetcher for PDF / Excel Export — uses get_all_paginated with a large per_page
  // so it respects current search + sort order exactly as shown in the table
  const fetchAllCustomerDataWithoutPagination = async () => {
    try {
      const response = await axios.get(
        `${baseUrl}customer_detail/get_all_paginated`,
        {
          params: {
            page: 1,
            per_page: 99999,
            search: debouncedSearch.trim() !== "" ? debouncedSearch : undefined,
            sort_field: sortField,
            sort_direction: sortDir,
          },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data.data || [];
    } catch (error) {
      console.error("Error fetching all customer data for export:", error);
      return [];
    }
  };

  // Table rows: footer appears ONLY on the last page, but shows grand totals of ALL pages
  const columnsData = useMemo<CustomerWithFooter[]>(() => {
    if (filteredItems.length === 0) return [];

    const totalPages = Math.ceil(totalRows / perPage) || 1;
    const isLastPage = currentPage >= totalPages;

    if (!isLastPage) return filteredItems;

    return [
      ...filteredItems,
      {
        isFooter: true,
        id: "footer",
        mobile: "",
        is_active: 1,
        currency: "",
        total_package_amount: grandTotals.totalPurchase,
        amount_spent: grandTotals.totalSpent,
        amount: grandTotals.totalBalance,
        total_package_amount_without_currency: grandTotals.totalPurchase,
        amount_spent_without_currency: grandTotals.totalSpent,
        amount_without_currency: grandTotals.totalBalance,
      },
    ];
  }, [filteredItems, grandTotals, currentPage, perPage, totalRows]);

  // Memoized DataTable Columns Definition
  const columns = useMemo(() => {
    const currencyHeader = currency ? ` (${currency})` : "";
    return [
      {
        name: <div>Sl.No</div>,
        cell: (row: CustomerWithFooter, index: number) => {
          if (row.isFooter) return "";
          return (currentPage - 1) * perPage + index + 1;
        },
        width: "75px",
      },
      {
        name: <div>Customer Mobile</div>,
        sortable: true,
        sortField: "mobile",
        cell: (row: CustomerWithFooter) => {
          if (row.isFooter) return <>Total</>;
          return row.mobile || "N/A";
        },
      },
      {
        name: <div>Total Purchase Amount{currencyHeader}</div>,
        sortable: true,
        sortField: "total_package_amount",
        cell: (row: CustomerWithFooter) => {
          if (row.isFooter) return grandTotals.totalPurchase;
          const val = row.total_package_amount_without_currency ?? row.total_package_amount;
          if (val === undefined || val === null) return "N/A";
          const cleanVal = String(val).replace(/[^0-9.]/g, "");
          return cleanVal !== "" ? cleanVal : "N/A";
        },
      },
      {
        name: <div>Amount Spent{currencyHeader}</div>,
        sortable: true,
        sortField: "amount_spent",
        cell: (row: CustomerWithFooter) => {
          if (row.isFooter) return grandTotals.totalSpent;
          const val = row.amount_spent_without_currency ?? row.amount_spent;
          if (val === undefined || val === null) return "N/A";
          const cleanVal = String(val).replace(/[^0-9.]/g, "");
          return cleanVal !== "" ? cleanVal : "N/A";
        },
      },
      {
        name: <div>Current Balance{currencyHeader}</div>,
        sortable: true,
        sortField: "amount",
        cell: (row: CustomerWithFooter) => {
          if (row.isFooter) return grandTotals.totalBalance;
          const val = row.amount_without_currency ?? row.amount;
          if (val === undefined || val === null) return "N/A";
          const cleanVal = String(val).replace(/[^0-9.]/g, "");
          return cleanVal !== "" ? cleanVal : "N/A";
        },
      },
    ];
  }, [currency, currentPage, perPage, grandTotals]);

  // Shared Helper for PDF & Excel Exporters
  const prepareExportData = async () => {
    const rawData = await fetchAllCustomerDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);

    if (!allData.length) {
      toast.warn("No customer data available for export.", {
        position: "top-right",
        autoClose: 1000,
      });
      return null;
    }

    let totalPurchase = 0;
    let totalSpent = 0;
    let totalBalance = 0;

    const rows = allData.map((row: any, idx: number) => {
      const purchVal = parseFloat(
        String(row.total_package_amount_without_currency ?? row.total_package_amount ?? "0").replace(/[^0-9.]/g, "")
      );
      const spentVal = parseFloat(
        String(row.amount_spent_without_currency ?? row.amount_spent ?? "0").replace(/[^0-9.]/g, "")
      );
      const balVal = parseFloat(
        String(row.amount_without_currency ?? row.amount ?? "0").replace(/[^0-9.]/g, "")
      );

      totalPurchase += isNaN(purchVal) ? 0 : purchVal;
      totalSpent += isNaN(spentVal) ? 0 : spentVal;
      totalBalance += isNaN(balVal) ? 0 : balVal;

      return {
        slNo: (idx + 1).toString(),
        mobile: row.mobile || "N/A",
        purchAmt: isNaN(purchVal) ? "0.00" : purchVal.toFixed(2),
        spentAmt: isNaN(spentVal) ? "0.00" : spentVal.toFixed(2),
        balAmt: isNaN(balVal) ? "0.00" : balVal.toFixed(2),
      };
    });

    return {
      rows,
      totalPurchase: totalPurchase.toFixed(2),
      totalSpent: totalSpent.toFixed(2),
      totalBalance: totalBalance.toFixed(2),
    };
  };

  const exportPDF = async () => {
    const exportData = await prepareExportData();
    if (!exportData) return;

    // Currency mapping to prevent PDF font encoding artifacts (e.g. ₹ -> Rs.)
    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currency ? currencyMap[currency] || currency : "";
    const pdfHeader = pdfCurrency ? ` (${pdfCurrency})` : "";

    const tableData = exportData.rows.map((r) => [
      r.slNo,
      r.mobile,
      r.purchAmt,
      r.spentAmt,
      r.balAmt,
    ]);

    const footerRow = [
      "",
      "Total",
      exportData.totalPurchase,
      exportData.totalSpent,
      exportData.totalBalance,
    ];

    exportToPDF({
      fileName: "Customer_Report",
      columns: [
        "Sl.No",
        "Customer Mobile",
        `Total Purchase Amount${pdfHeader}`,
        `Amount Spent${pdfHeader}`,
        `Current Balance${pdfHeader}`,
      ],
      data: tableData,
      emptyDataMessage: "No customer data available for export.",
      footerRow,
    });
  };

  const exportExcel = async () => {
    const exportData = await prepareExportData();
    if (!exportData) return;

    const excelHeader = currency ? ` (${currency})` : "";
    const purchHeader = `Total Purchase Amount${excelHeader}`;
    const spentHeader = `Amount Spent${excelHeader}`;
    const balHeader = `Current Balance${excelHeader}`;

    const tableData = exportData.rows.map((r) => ({
      "Sl.No": r.slNo,
      "Customer Mobile": r.mobile,
      [purchHeader]: r.purchAmt,
      [spentHeader]: r.spentAmt,
      [balHeader]: r.balAmt,
    }));

    const footerRow = {
      "Sl.No": "",
      "Customer Mobile": "Total",
      [purchHeader]: exportData.totalPurchase,
      [spentHeader]: exportData.totalSpent,
      [balHeader]: exportData.totalBalance,
    };

    excelExportUtils(tableData, "Customer_Report", "Customers Summary", footerRow);
  };

  return (
    <>
      <Breadcrumbs pageTitle="Customer Report" parent="Customer" title="Customer Report" />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            <Row className="align-items-center justify-content-between g-2">
              <Col xl={2}>
                <div className="d-flex gap-2">
                  {hasPermission("customer-report.download_pdf") && (
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="Export PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                  )}
                  {hasPermission("customer-report.download_excel") && (
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="Export Excel"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  )}
                </div>
              </Col>

              <Col xl={3}>
                <div className="d-flex align-items-center">
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={filterText}
                    onChange={(e) => {
                      setFilterText(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar mt-3">
              <DataTable
                data={columnsData}
                columns={columns}
                highlightOnHover
                striped
                customStyles={customStyles}
                pagination
                paginationServer
                paginationPerPage={perPage}
                paginationTotalRows={totalRows}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                paginationDefaultPage={currentPage}
                responsive
                sortServer
                onSort={handleSort}
                conditionalRowStyles={conditionalRowStyles}
                progressPending={tableLoading}
                progressComponent={
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: "300px", width: "100%" }}
                  >
                    <Spinner color="primary" />
                  </div>
                }
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
